import {
  useState,
  useRef,
  useEffect,
  type FC,
  type ComponentType,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Filter, X } from 'lucide-react';
import CheckAnimatedIcon from '@/components/ui/CheckAnimatedIcon';
import XMarkAnimatedIcon from '@/components/ui/XMarkAnimatedIcon';
import MultiSelectDropdownRaw from '@/components/ui/MultiSelectDropdown';
import AsyncMultiSelectDropdownRaw from '@/components/ui/AsyncMultiSelectDropdown';

// Single-step casts at import boundary: sibling dropdowns have partially inferred / stricter
// prop shapes (MultiSelectDropdown requires `onClear`; AsyncMultiSelectDropdown is JS-inferred).
// Cast to permissive ComponentType so this hub can forward its richer prop shapes without
// modifying those files. To be dropped in D.3.4 when those siblings are typed.
const MultiSelectDropdown = MultiSelectDropdownRaw as unknown as ComponentType<
  Record<string, unknown>
>;
const AsyncMultiSelectDropdown = AsyncMultiSelectDropdownRaw as unknown as ComponentType<
  Record<string, unknown>
>;

// -----------------------------------------------------------------------------
// File-local companion types (HUB superset — every field optional so 8 consumers
// compile via their existing `as unknown as ComponentType<Record<string, unknown>>`
// boundary casts. Do NOT tighten without auditing every consumer.)
// -----------------------------------------------------------------------------

type FilterOptionId = string | number | boolean;

interface FilterOption {
  id?: FilterOptionId;
  label?: string;
}

type LookupsMap = Record<string, unknown>;
type DistinctValuesMap = Record<string, FilterOption[] | undefined>;

type FilterValueEntry = unknown;
type FilterValuesMap = Record<string, FilterValueEntry>;

// `type` left as `string` (not a literal union) so inline consumer defs where
// filter objects are declared with `type: 'multiselect'` widen to `string` still
// assign to FilterDef[]. Narrowing is performed inside the component via equality.
interface FilterDef {
  field?: string;
  label?: string;
  type?: string;
  options?: FilterOption[];
  getOptions?: (
    data: unknown[],
    lookups: LookupsMap,
    distinctValues: DistinctValuesMap,
  ) => FilterOption[] | undefined;
  getLabel?: (
    valueId: FilterOptionId,
    lookups: LookupsMap,
    distinctValues: DistinctValuesMap,
  ) => string | null | undefined;
  allowNullFilter?: boolean;
  onSearch?: (
    query: string,
    tempValues: FilterValuesMap,
  ) => Promise<FilterOption[]> | FilterOption[];
  debounceMs?: number;
  minChars?: number;
  placeholder?: string;
}

// HUB superset — every field optional, callbacks use method syntax so bivariant
// parameter checking accepts `Dispatch<SetStateAction<T>>` for any consumer-shaped T.
export interface AdvancedFiltersPopupProps {
  filters?: FilterDef[];
  values?: Record<string, unknown>;
  // Param typed `never` so any consumer callback shape `(v: ConcreteT) => void`
  // assigns via contravariance — including Dispatch<SetStateAction<ConcreteT>>.
  // Internal invocations use the `invokeOnChange` boundary helper below.
  onChange?(newValues: never): void;
  data?: unknown[];
  lookups?: LookupsMap;
  distinctValues?: DistinctValuesMap;
  showHeader?: boolean;
  headerLabel?: string;
}

export interface FilterChipsProps {
  filters?: FilterDef[];
  values?: Record<string, unknown>;
  // Param typed `never` so any consumer callback shape `(v: ConcreteT) => void`
  // assigns via contravariance — including Dispatch<SetStateAction<ConcreteT>>.
  // Internal invocations use the `invokeOnChange` boundary helper below.
  onChange?(newValues: never): void;
  data?: unknown[];
  lookups?: LookupsMap;
  distinctValues?: DistinctValuesMap;
}

// Boundary helper: Props declares `onChange?(v: never)` so consumer callbacks of
// any concrete shape assign via contravariance. Inside this file we invoke the
// callback with our internal map, narrowing via a single-step unknown cast.
type OnChangeLoose = (newValues: Record<string, unknown>) => void;
const invokeOnChange = (
  cb: ((newValues: never) => void) | undefined,
  value: Record<string, unknown>,
): void => {
  if (cb) {
    (cb as unknown as OnChangeLoose)(value);
  }
};

interface VisibleChip {
  filter: FilterDef;
  valueId: FilterOptionId;
  chipLabel: string;
  isLoading?: boolean;
}

// -----------------------------------------------------------------------------
// Default export: AdvancedFiltersPopup
// -----------------------------------------------------------------------------

const AdvancedFiltersPopup: FC<AdvancedFiltersPopupProps> = ({
  filters = [],
  values = {},
  onChange,
  data = [],
  lookups = {},
  distinctValues = {},
  showHeader = false,
  headerLabel = 'Filtros',
}) => {
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [tempValues, setTempValues] = useState<FilterValuesMap>({});
  const popupRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Inicializar tempValues quando abrir popup
  useEffect(() => {
    if (showPopup) {
      setTempValues({ ...values });
    }
  }, [showPopup, values]);

  // Fechar popup ao clicar fora (exceto no botão de filtro)
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target as Node | null;
      // Ignorar cliques no botão de filtro (o próprio botão faz o toggle)
      if (buttonRef.current && target && buttonRef.current.contains(target)) {
        return;
      }
      // Fechar se clicar fora do popup
      if (popupRef.current && target && !popupRef.current.contains(target)) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopup]);

  // Contar filtros ativos
  const countActiveFilters = (): number => {
    return Object.values(values).reduce<number>((count, arr) => {
      return count + (Array.isArray(arr) ? arr.length : (arr ? 1 : 0));
    }, 0);
  };

  const hasActiveFilters = countActiveFilters() > 0;

  // Obter opções para um filtro
  // Adiciona automaticamente a opção "(Vazio)" para filtrar valores null
  // Filtra opções baseado nos distinctValues (valores que existem nos dados filtrados)
  const getFilterOptions = (filter: FilterDef): FilterOption[] => {
    let options: FilterOption[] = [];
    if (filter.options) {
      options = [...filter.options];
    } else if (filter.getOptions) {
      options = filter.getOptions(data, lookups, distinctValues) || [];
    }

    // Se há distinctValues para este campo, filtrar opções para mostrar apenas valores existentes
    // IMPORTANTE: Se distinctValues[field] existe (mesmo vazio), significa que o servidor
    // retornou os valores válidos - devemos filtrar. Se for array vazio, não há valores válidos.
    const fieldDistinct = filter.field ? distinctValues[filter.field] : undefined;
    if (fieldDistinct !== undefined) {
      if (fieldDistinct.length > 0) {
        const distinctIds = new Set<FilterOptionId>(
          fieldDistinct.map((d) => d.id).filter((id): id is FilterOptionId => id !== undefined),
        );
        options = options.filter((opt) => opt.id !== undefined && distinctIds.has(opt.id));
      } else {
        // Array vazio = não há valores válidos, remover todas as opções exceto __null__
        options = [];
      }
    }

    // Adicionar opção de filtrar por null (a menos que explicitamente desabilitado)
    if (filter.allowNullFilter !== false && filter.type === 'multiselect') {
      // Verificar se já não existe uma opção __null__
      const hasNullOption = options.some((opt) => opt.id === '__null__');
      if (!hasNullOption) {
        options = [{ id: '__null__', label: '(Vazio)' }, ...options];
      }
    }

    return options;
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    invokeOnChange(onChange, tempValues);
    setShowPopup(false);
  };

  // Limpar filtros temporários
  const limparFiltrosTemp = () => {
    const emptyValues: FilterValuesMap = {};
    filters.forEach((f) => {
      if (f.field) {
        emptyValues[f.field] = [];
      }
    });
    setTempValues(emptyValues);
  };

  const readFieldValues = (source: FilterValuesMap, field: string | undefined): FilterOptionId[] => {
    if (!field) return [];
    const raw = source[field];
    return Array.isArray(raw) ? raw : [];
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Botão de Filtros */}
      <button
        ref={buttonRef}
        className={`btn-filter ${hasActiveFilters ? 'btn-filter--has-filters' : ''}`}
        onClick={() => setShowPopup(!showPopup)}
        style={{ borderRadius: '999px' }}
      >
        <Filter size={16} style={hasActiveFilters ? { color: 'var(--color-primary)' } : undefined} />
        Filtros
      </button>

      {/* Popup de Filtros */}
      {showPopup && (
        <div ref={popupRef} className="advanced-filters__popup">
          {/* Header com botões */}
          <div className="advanced-filters__header">
            {showHeader && <span className="advanced-filters__title">{headerLabel}</span>}
            <div className="advanced-filters__header-actions">
              <button className="btn-add xmark-animated" onClick={limparFiltrosTemp}>
                <XMarkAnimatedIcon size={14} />{' '}
                <span className="btn-text" style={{ color: 'var(--muted-2)' }}>
                  Limpar todos
                </span>
              </button>
              <button className="btn-add checkmark-animated" onClick={aplicarFiltros}>
                <CheckAnimatedIcon size={14} /> <span className="btn-text">Aplicar</span>
              </button>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="advanced-filters__content">
            {filters.map((filter) => {
              const field = filter.field || '';
              const tempSelected = readFieldValues(tempValues, field);
              const activeCount = readFieldValues(values, field).length;

              return (
                <div key={field}>
                  {filter.type === 'multiselect' && (
                    <MultiSelectDropdown
                      label={filter.label}
                      options={getFilterOptions(filter)}
                      selectedValues={tempSelected}
                      onChange={(selectedIds: FilterOptionId[]) => {
                        setTempValues((prev) => ({ ...prev, [field]: selectedIds }));
                      }}
                      hasActiveFilter={activeCount > 0}
                    />
                  )}

                  {filter.type === 'asyncMultiselect' && filter.onSearch && (
                    <AsyncMultiSelectDropdown
                      label={filter.label}
                      selectedValues={tempSelected}
                      onChange={(selectedIds: FilterOptionId[]) => {
                        setTempValues((prev) => ({ ...prev, [field]: selectedIds }));
                      }}
                      onSearch={(query: string) =>
                        filter.onSearch ? filter.onSearch(query, tempValues) : []
                      }
                      hasActiveFilter={activeCount > 0}
                      debounceMs={filter.debounceMs || 300}
                      minChars={filter.minChars || 0}
                      placeholder={filter.placeholder || 'Todos'}
                    />
                  )}

                  {filter.type === 'select' && (
                    <div className="ac-field">
                      <label className="ac-field__label">{filter.label}</label>
                      <select
                        className="ac-select ac-select--sm"
                        value={String(tempSelected[0] ?? '')}
                        onChange={(e) => {
                          setTempValues((prev) => ({
                            ...prev,
                            [field]: e.target.value ? [e.target.value] : [],
                          }));
                        }}
                      >
                        <option value="">Todos</option>
                        {getFilterOptions(filter).map((opt) => (
                          <option key={String(opt.id)} value={String(opt.id ?? '')}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {filter.type === 'boolean' && (
                    <div className="ac-field">
                      <label className="ac-checkbox-label">
                        <input
                          type="checkbox"
                          className="ac-checkbox"
                          checked={tempSelected[0] === true}
                          onChange={(e) => {
                            setTempValues((prev) => ({
                              ...prev,
                              [field]: e.target.checked ? [true] : [],
                            }));
                          }}
                        />
                        {filter.label}
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFiltersPopup;

// -----------------------------------------------------------------------------
// Named export: FilterChips
// -----------------------------------------------------------------------------

export const FilterChips: FC<FilterChipsProps> = ({
  filters = [],
  values = {},
  onChange,
  data = [],
  lookups = {},
  distinctValues = {},
}) => {
  // Contar filtros ativos
  const hasActiveFilters = Object.values(values).some((arr) =>
    Array.isArray(arr) ? arr.length > 0 : !!arr,
  );

  if (!hasActiveFilters) return null;

  // Obter opções para um filtro (para chips não filtramos por distinctValues)
  const getFilterOptions = (filter: FilterDef): FilterOption[] => {
    let options: FilterOption[] = [];
    if (filter.options) {
      options = [...filter.options];
    } else if (filter.getOptions) {
      options = filter.getOptions(data, lookups, distinctValues) || [];
    }

    // Adicionar opção de filtrar por null (a menos que explicitamente desabilitado)
    if (filter.allowNullFilter !== false && filter.type === 'multiselect') {
      const hasNullOption = options.some((opt) => opt.id === '__null__');
      if (!hasNullOption) {
        options = [{ id: '__null__', label: '(Vazio)' }, ...options];
      }
    }

    return options;
  };

  // Gerar label para chip
  const getChipLabel = (filter: FilterDef, valueId: FilterOptionId): string | null => {
    // Primeiro tenta usar getLabel do filtro (usa lookups e distinctValues)
    if (filter.getLabel) {
      const label = filter.getLabel(valueId, lookups, distinctValues);
      if (label) return label;
    }
    // Fallback: usar getOptions (depende de data)
    const options = getFilterOptions(filter);
    const option = options.find((o) => o.id === valueId);
    return option && option.label ? option.label : null; // null indica que não encontrou
  };

  // Remover um filtro específico
  const removerFiltro = (field: string | undefined, valueId: FilterOptionId) => {
    if (!field) return;
    const newValues: FilterValuesMap = { ...values };
    const current = newValues[field];
    if (Array.isArray(current)) {
      newValues[field] = current.filter((v) => v !== valueId);
    } else {
      newValues[field] = [];
    }
    invokeOnChange(onChange, newValues);
  };

  // Limpar todos os filtros
  const limparTodosFiltros = () => {
    const emptyValues: FilterValuesMap = {};
    filters.forEach((f) => {
      if (f.field) {
        emptyValues[f.field] = [];
      }
    });
    invokeOnChange(onChange, emptyValues);
  };

  // Pré-calcular os chips que serão visíveis
  const visibleChips: VisibleChip[] = [];
  filters.forEach((filter) => {
    const field = filter.field;
    if (!field) return;
    const filterValues = values[field];
    if (!Array.isArray(filterValues) || filterValues.length === 0) return;

    filterValues.forEach((valueId) => {
      const chipLabel = getChipLabel(filter, valueId);
      if (chipLabel) {
        visibleChips.push({ filter, valueId, chipLabel });
      }
    });
  });

  // Se não há chips visíveis, não renderiza nada
  if (visibleChips.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      <span style={{ fontSize: '12px', color: 'var(--muted)' }}>Filtros aplicados:</span>

      {visibleChips.map(({ filter, valueId, chipLabel, isLoading }) => (
        <span
          key={`${filter.field}-${String(valueId)}`}
          style={{
            background: 'var(--color-primary-light)',
            color: '#0f3460',
            padding: '4px 10px',
            borderRadius: '16px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            border: '1px solid #bfdbfe',
            opacity: isLoading ? 0.6 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          {filter.label}: {chipLabel}
          <X
            size={14}
            style={{ cursor: 'pointer' }}
            onClick={(_e: ReactMouseEvent<SVGSVGElement>) => removerFiltro(filter.field, valueId)}
          />
        </span>
      ))}

      <button
        className="btn-add xmark-animated"
        onClick={limparTodosFiltros}
        style={{ fontSize: '12px' }}
      >
        <XMarkAnimatedIcon size={12} /> <span className="btn-text">Limpar filtros</span>
      </button>
    </div>
  );
};
